/**
 * Returns a configured service root as an absolute URL.
 *
 * Some environments configure a service root as a relative path (e.g.
 * `REACT_APP_MAP_SERVICE=/map/api/v1` behind a dev proxy). Mapbox GL fetches
 * vector tiles from a web worker that silently drops relative URLs — the layer
 * renders nothing, with no error — so tile URL templates have to be absolute.
 *
 * The auth predicate that decides whether to attach the end-user Bearer token
 * (`shouldAttachMapAuth`) tests the request URL against the same service root,
 * so it has to absolutize it exactly the same way. Both sides call this one
 * helper: if they drift, a relative service root produces absolute tile URLs
 * that fail the prefix test, and every tile 401s.
 *
 * @param configured The raw env value; may be absolute, relative, or absent.
 * @returns The value unchanged when it is already absolute or empty, otherwise
 *          the same path resolved against the page origin.
 */
export function absolutizeServiceRoot(configured: Maybe<string>): string {
  const root = configured ?? "";
  if (!root || root.startsWith("http")) {
    return root;
  }
  return `${window.location.origin}${root}`;
}
